import * as ToneJS from 'tone';
import { Frequency } from 'tone/build/esm/core/type/Units';
import { Monophonic } from 'tone/build/esm/instrument/Monophonic';
import { RydInternalInstrument } from '../../model/instrument';
import {
    Note,
    NoteVelocity
} from '../../model/core';
import { TrackOutputType } from '../../model/tracks';


export interface OscConfig {
    type: ToneJS.ToneOscillatorType;
    frequency: Frequency;
    mix: number;
    detune: number;
}

export class ThreeVoiceSynth<SynthOptions> extends Monophonic<ToneJS.SynthOptions> implements RydInternalInstrument {

    id: string = '3 voice synth';
    name: string;
    frequency: ToneJS.Signal<'frequency'>;
    detune: ToneJS.Signal<'cents'>;
    outputNode: ToneJS.OutputNode;

    private oscillators: ToneJS.OmniOscillator<any>[] = [];
    private mergedOscillator: ToneJS.Merge;
    private filter: ToneJS.Filter;
    private envelope: ToneJS.AmplitudeEnvelope;
    private delay: ToneJS.FeedbackDelay;
    private chorus: ToneJS.Chorus;
    private reverb: ToneJS.Reverb;
    // private chainEnd: ToneJS.OutputNode;

    static getFactory(): (ac: AudioContext) => RydInternalInstrument {
        return (ac: AudioContext) => {
            const oscConfigs: OscConfig[] = [
                { type: "sine", frequency: 440, mix: 0.5, detune: 0 },
                { type: "sawtooth", frequency: 440, mix: 0.5, detune: 0 },
                { type: "triangle", frequency: 440, mix: 0.5, detune: 0 },
            ];

            const filterConfig: ToneJS.FilterOptions = {
                ...ToneJS.Filter.getDefaults(),
                type: "lowpass",
                frequency: 800,
                Q: 1,
            };

            const envConfig: ToneJS.EnvelopeOptions = {
                ...ToneJS.AmplitudeEnvelope.getDefaults(),
                attack: 0.01,
                decay: 0.2,
                sustain: 0.7,
                release: 0.5,
            };

            const delayConfig: ToneJS.DelayOptions = {
                ...ToneJS.FeedbackDelay.getDefaults(),
                delayTime: 0.25
            };

            const chorusConfig: ToneJS.ChorusOptions = {
                ...ToneJS.Chorus.getDefaults(),
                frequency: 1.5,
                delayTime: 3.5,
                depth: 0.7,
                feedback: 0.1,
                type: "sine",
            };

            const reverbConfig = {
                decay: 1.5,
                preDelay: 0.01
            };
            const synth = new ThreeVoiceSynth(oscConfigs, filterConfig, envConfig, delayConfig, chorusConfig, reverbConfig);
            return synth;
        }
    }

    public constructor(
        private oscConfigs: OscConfig[],
        private filterConfig: ToneJS.FilterOptions,
        private envConfig: ToneJS.EnvelopeOptions,
        private delayConfig: ToneJS.DelayOptions & {wet?: number} = ToneJS.FeedbackDelay.getDefaults(),
        private chorusConfig: ToneJS.ChorusOptions = ToneJS.Chorus.getDefaults(),
        private reverbConfig: {decay: number, preDelay: number} = {decay: 1.5, preDelay: 0.01},
        { standaloneOutput }: { standaloneOutput?: boolean } = { standaloneOutput: false }
    ) {

        const options = ToneJS.optionsFromArguments(ToneJS.Synth.getDefaults(), arguments);
        super(options);

        this.mergedOscillator = new ToneJS.Merge(oscConfigs.length);
        // Set up oscillators and connect each to the mixer
        this.oscConfigs.forEach((cfg, i) => {
            const osc = new ToneJS.OmniOscillator(cfg.frequency, cfg.type);
            this.oscillators.push(osc);
            osc.connect(this.mergedOscillator).start();
        });
        this.frequency = this.oscillators[0].frequency;
        this.oscillators.forEach((osc, i) => {
            osc.frequency.value = this.frequency.value;
            if (osc.detune && this.oscConfigs[i].detune) {
                osc.detune.value = this.oscConfigs[i].detune;
            }
        });

        // Filter and envelope
        this.envelope = new ToneJS.AmplitudeEnvelope(envConfig);

        // Routing: mixer -> filter -> envelope -> output

        this.oscillators.forEach((osc) => {
            try { osc.disconnect(); } catch { }
            osc.connect(this.mergedOscillator, 0, 0); // left
            osc.connect(this.mergedOscillator, 0, 1); // right
        });
        // Initialize filter and insert into the signal chain: osc -> filter -> env -> delay -> chorus -> reverb -> dest
        this.filter = new ToneJS.Filter(this.filterConfig);

        // Effects after the envelope
        this.delay = new ToneJS.FeedbackDelay(this.delayConfig);
        this.chorus = new ToneJS.Chorus(this.chorusConfig).start();
        this.reverb = new ToneJS.Reverb(this.reverbConfig);

        // Chain with added effects
        this.mergedOscillator.chain(this.filter, this.envelope, this.delay, this.chorus, this.reverb);
        if (standaloneOutput) {
            this.outputNode = ToneJS.getDestination();
            this.reverb.connect(this.outputNode);
        } else {
            this.outputNode = this.reverb;
        }
    }

    getInstrumentDescription() {
        return {
            id: this.id,
            name: this.name
        };
    }

    noteOn(note: Note, velocity: NoteVelocity) {
        // FIXME: handle velocity
        this.frequency.setValueAtTime(ToneJS.Frequency(note.getName()).toFrequency(), ToneJS.now());
        this.triggerAttack(note.getName());
    };
    noteOff(note: Note) {
        this.triggerRelease(ToneJS.now());
    }
    getOutputType = () => 'VSYNTH' as TrackOutputType;
    getOutputNode(): ToneJS.OutputNode {
        return this.outputNode;
    }

    protected _triggerEnvelopeAttack(time: ToneJS.Unit.Seconds, velocity: ToneJS.Unit.NormalRange): void {
        this.oscillators.forEach(osc => {
            osc.frequency.setValueAtTime(this.frequency.value, time);
        });

        this.oscillators.forEach(osc => {
            osc.start(time);
        });
        this.envelope.triggerAttack(time, velocity);
        // if there is no release portion, stop the oscillator
        if (this.envelope.sustain === 0) {
            const computedAttack = this.toSeconds(this.envelope.attack);
            const computedDecay = this.toSeconds(this.envelope.decay);
            this.oscillators.forEach(osc => {
                osc.stop(time + computedAttack + computedDecay);
            });
        }
    }
    protected _triggerEnvelopeRelease(time: ToneJS.Unit.Seconds): void {
        this.envelope.triggerRelease(time);
    }

    triggerInSequence: (notes: Note[], duration: string, velocity: NoteVelocity, time: any) => void = (notes, _duration, velocity, time) => {
        notes.forEach(n => this.triggerAttackRelease(n.getName(), _duration, time, velocity / 128));
    };

    getLevelAtTime(time: ToneJS.Unit.Time): ToneJS.Unit.NormalRange {
        throw new Error('Method not implemented.');
    }

    updateVoiceSettings(index: number, options: Partial<OscConfig> ) {
        this.oscConfigs[index - 1] = { ...this.oscConfigs[index - 1], ...options };
        const osc = this.oscillators[index - 1];
        if (options.type) {
            osc.type = options.type;
        }
        if (options.frequency) {
            osc.frequency.value = options.frequency;
        }
        if (options.mix !== undefined) {
            osc.volume.value = ToneJS.gainToDb(options.mix);
        }
        if (options.detune !== undefined) {
            osc.detune.value = options.detune;
        }
    }

    getOscillatorSettings(index: number): OscConfig {
        return this.oscConfigs[index - 1];
    }
    updateEnvelopeSettings(options: Partial<ToneJS.EnvelopeOptions>) {
        this.envConfig = { ...this.envConfig, ...options };
        this.envelope.set(this.envConfig);
    }

    getEnvelopeSettings(): ToneJS.EnvelopeOptions {
        return this.envConfig;
    }

    updateFilterSettings(options: Partial<ToneJS.FilterOptions>) {
        this.filterConfig = { ...this.filterConfig, ...options };
        this.filter.set(this.filterConfig);
    }

    getFilterSettings(): ToneJS.FilterOptions {
        return this.filterConfig;
    }

    updateDelaySettings(options: Partial<ToneJS.DelayOptions & {wet?: number}>) {
        this.delayConfig = { ...this.delayConfig, ...options };
        this.delay.set(this.delayConfig);
        if (options.wet !== undefined) {
            this.delay.wet.value = options.wet;
        }
    }

    getDelaySettings(): ToneJS.DelayOptions & {wet?: number} {
        return {...this.delayConfig, wet: this.delay.wet.value};
    }

    updateChorusSettings(options: Partial<ToneJS.ChorusOptions>) {
        this.chorusConfig = { ...this.chorusConfig, ...options };
        this.chorus.set(this.chorusConfig);
    }

    getChorusSettings(): ToneJS.ChorusOptions {
        return this.chorusConfig;
    }

    updateReverbSettings(options: Partial<{decay: number, preDelay: number, wet: number}>) {
        this.reverbConfig = { ...this.reverbConfig, ...options };
        this.reverb.set(this.reverbConfig);
        if (options.wet !== undefined) {
            this.reverb.wet.value = options.wet;
        }
    }

    getReverbSettings(): {decay: number, preDelay: number, wet: number} {
        return {...this.reverbConfig, wet: this.reverb.wet.value};
    }
}
